import React, { useState } from 'react';
import { UserCriteria } from '../services/recommendationService';

interface BasicRecommendationFormProps {
  onSubmit: (criteria: UserCriteria) => void;
  loading?: boolean;
  className?: string;
}

const BasicRecommendationForm: React.FC<BasicRecommendationFormProps> = ({
  onSubmit,
  loading = false,
  className = ""
}) => {
  const [criteria, setCriteria] = useState<UserCriteria>({
    equipment_type: 'woods',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(criteria);
  };

  const updateCriteria = (updates: Partial<UserCriteria>) => {
    setCriteria(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className={`max-w-3xl mx-auto ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Equipment Type Selection */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            What are you looking for?
          </h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {(['woods', 'irons', 'wedges'] as const).map((type) => (
              <label
                key={type}
                className={`relative flex cursor-pointer rounded-lg border p-3 sm:p-4 focus:outline-none ${
                  criteria.equipment_type === type
                    ? 'border-green-ink ring-2 ring-green-ink bg-green/10'
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="equipment_type"
                  value={type}
                  checked={criteria.equipment_type === type}
                  onChange={(e) => updateCriteria({ equipment_type: e.target.value as any })}
                  className="sr-only"
                />
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <span className="block text-sm font-semibold text-gray-900 capitalize">
                      {type === 'woods' ? 'Driver' : type === 'irons' ? 'Irons' : 'Wedges'}
                    </span>
                    <span className="mt-0.5 text-xs text-gray-500 hidden sm:block">
                      {type === 'woods' && 'Drivers, fairway woods'}
                      {type === 'irons' && 'Iron sets'}
                      {type === 'wedges' && 'Sand, gap, lob'}
                    </span>
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Player Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Player Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="handicap" className="block text-sm font-medium text-gray-700">
                Handicap
              </label>
              <input
                type="number"
                id="handicap"
                min="0"
                max="54"
                value={criteria.handicap || ''}
                onChange={(e) => updateCriteria({ 
                  handicap: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                placeholder="e.g., 12"
              />
            </div>
            
            <div>
              <label htmlFor="age" className="block text-sm font-medium text-gray-700">
                Age
              </label>
              <input
                type="number"
                id="age"
                min="10"
                max="100"
                value={criteria.age || ''}
                onChange={(e) => updateCriteria({ 
                  age: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                placeholder="e.g., 35"
              />
            </div>

            <div>
              <label htmlFor="club_speed" className="block text-sm font-medium text-gray-700">
                Club Speed with Driver (mph)
              </label>
              <select
                id="club_speed"
                value={criteria.swing_speed_mph || ''}
                onChange={(e) => updateCriteria({ 
                  swing_speed_mph: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select club speed</option>
                <option value="55">&lt;60 mph</option>
                <option value="65">60-70 mph</option>
                <option value="75">70-80 mph</option>
                <option value="85">80-90 mph</option>
                <option value="95">90-100 mph</option>
                <option value="105">100-110 mph</option>
                <option value="115">110+ mph</option>
              </select>
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                Gender
              </label>
              <select
                id="gender"
                value={criteria.gender || ''}
                onChange={(e) => updateCriteria({ 
                  gender: e.target.value || undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Playing Profile */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Playing Profile
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="shot_shape" className="block text-sm font-medium text-gray-700">
                Shot Shape
              </label>
              <select
                id="shot_shape"
                value={criteria.shot_shape || ''}
                onChange={(e) => updateCriteria({ 
                  shot_shape: e.target.value || undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select shot shape</option>
                <option value="draw">Draw</option>
                <option value="fade">Fade</option>
                <option value="both">Both</option>
                <option value="straight">Straight</option>
                <option value="erratic">Erratic</option>
              </select>
            </div>

            <div>
              <label htmlFor="ball_flight" className="block text-sm font-medium text-gray-700">
                Preferred Ball Flight
              </label>
              <select
                id="ball_flight"
                value={criteria.preferred_ball_flight || ''}
                onChange={(e) => updateCriteria({ 
                  preferred_ball_flight: e.target.value || undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select ball flight</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label htmlFor="rounds_per_month" className="block text-sm font-medium text-gray-700">
                Golf Rounds per Month
              </label>
              <select
                id="rounds_per_month"
                value={criteria.rounds_per_month || ''}
                onChange={(e) => updateCriteria({ 
                  rounds_per_month: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select frequency</option>
                <option value="1">1-2 rounds</option>
                <option value="3">3-4 rounds</option>
                <option value="6">5-8 rounds</option>
                <option value="10">9-12 rounds</option>
                <option value="15">13+ rounds</option>
              </select>
            </div>

            <div>
              <label htmlFor="years_playing" className="block text-sm font-medium text-gray-700">
                Years Playing Golf
              </label>
              <select
                id="years_playing"
                value={criteria.years_playing || ''}
                onChange={(e) => updateCriteria({ 
                  years_playing: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Select experience</option>
                <option value="1">Less than 1 year</option>
                <option value="2">1-2 years</option>
                <option value="5">3-5 years</option>
                <option value="10">6-10 years</option>
                <option value="15">11-15 years</option>
                <option value="20">16-20 years</option>
                <option value="25">20+ years</option>
              </select>
            </div>
          </div>
        </div>

        {/* Simple Preferences */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            What's most important to you?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="primary_goal" className="block text-sm font-medium text-gray-700">
                Primary Goal
              </label>
              <select
                id="primary_goal"
                value={criteria.primary_goal || ''}
                onChange={(e) => updateCriteria({ 
                  primary_goal: e.target.value as any || undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">What matters most?</option>
                <option value="distance">More Distance</option>
                <option value="accuracy">Better Accuracy</option>
                <option value="consistency">More Consistent</option>
                <option value="feel">Better Feel</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="budget_max" className="block text-sm font-medium text-gray-700">
                Budget (optional)
              </label>
              <select
                id="budget_max"
                value={criteria.budget_max || ''}
                onChange={(e) => updateCriteria({ 
                  budget_max: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
              >
                <option value="">Any budget</option>
                <option value="300">Under $300</option>
                <option value="500">Under $500</option>
                <option value="750">Under $750</option>
                <option value="1000">Under $1000</option>
                <option value="1500">Under $1500</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className={`px-8 py-3 rounded-md text-white font-semibold text-lg ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-ink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-green-ink focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Getting Recommendations...
              </span>
            ) : (
              'Get Quick Recommendations'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicRecommendationForm;